import i18next from "i18next";
import moment from "moment";
import { t } from "i18next";
import { IRoutineFrequency, IRoutineRepeatType } from "@propely/propely-typings";
import { getCurrentNormalizedLanguageCode } from "@src/libs/i18next/language";

export class FrequencyUtilities {
  static convertDateToString(date: Date): string {
    const dd = date.getUTCDate();
    const mm = date.getUTCMonth() + 1;
    const yyyy = date.getUTCFullYear();
    let returnDate = yyyy + ".";

    if (mm < 10) {
      returnDate += `0${mm}.`;
    } else {
      returnDate += `${mm}.`;
    }
    if (dd < 10) {
      returnDate += `0${dd}`;
    } else {
      returnDate += `${dd}`;
    }
    return returnDate;
  }

  /**
   * Splits a string date into an array of strings for year, month and day, by checking different separators
   * @param date The date in string format
   */
  private static splitDate(date: string): string[] {
    let fields: string[] = [];
    if (date.includes("-")) {
      fields = date.split("-");
    } else if (date.includes("/")) {
      fields = date.split("/");
    } else if (date.includes(".")) {
      fields = date.split(".");
    }
    return fields.map((f) => f);
  }

  static convertStringToDate(str: string): Date {
    const date = new Date();
    const fields = this.splitDate(str);
    const dd = fields[2];
    const mm = fields[1];
    const yyyy = fields[0];
    date.setUTCFullYear(+yyyy);
    if (mm[0] === "0") {
      date.setUTCMonth(+mm.substring(1) - 1);
    } else {
      date.setUTCMonth(+mm - 1);
    }
    if (dd[0] === "0") {
      date.setUTCDate(+dd.substring(1));
    } else {
      date.setUTCDate(+dd);
    }

    return date;
  }

  static addToDate(date: Date, daysToAdd: number): Date {
    const newDate = new Date(date.getTime());
    newDate.setUTCDate(date.getUTCDate() + daysToAdd);
    return newDate;
  }
  static addMonthToDate(date: Date, monthsToAdd: number): Date {
    const newDate = new Date(date.getTime());
    newDate.setUTCMonth(date.getUTCMonth() + monthsToAdd);
    return newDate;
  }

  static getPossibleDates(
    frequency: IRoutineFrequency,
    routineStartDate: Date,
    futureDate: Date,
    now: Date,
    each: number,
    createActivitiesInPast: boolean
  ) {
    const possibleDates: string[] = [];

    if (frequency.daily) {
      //1000*24*3600
      const routineStartIndex = Math.round(routineStartDate.getTime() / 86400000) % each;
      const date = new Date(now.getTime());
      while (date < futureDate) {
        if (Math.round(date.getTime() / 86400000) % each === routineStartIndex) {
          if (date <= futureDate && (createActivitiesInPast ? date >= routineStartDate : date >= now)) {
            possibleDates.push(this.convertDateToString(date));
          }
          date.setUTCDate(date.getUTCDate() + each);
        } else {
          date.setUTCDate(date.getUTCDate() + 1);
        }
      }
    } else if (frequency.weekly) {
      //1000*24*3600*7
      const routineStartIndex = Math.round(routineStartDate.getTime() / 604800000) % each;
      const date = new Date(now.getTime());
      do {
        const weekday = date.getUTCDay();
        if (
          Math.round(date.getTime() / 604800000) % each === routineStartIndex &&
          frequency.weekly.days.includes(weekday) &&
          date <= futureDate &&
          (createActivitiesInPast ? date >= routineStartDate : date >= now)
        ) {
          possibleDates.push(this.convertDateToString(date));
        }
        date.setUTCDate(date.getUTCDate() + 1);
      } while (date <= futureDate);
    } else if (frequency.monthly) {
      const monthAfterFutureDate = this.addMonthToDate(futureDate, 1);
      const date = new Date(now.getTime());
      date.setUTCDate(1);

      while (date <= monthAfterFutureDate) {
        const newDate = new Date(date.getTime());
        const monthsDiff = this.monthDiff(routineStartDate, newDate);
        if (monthsDiff >= 0 && monthsDiff % each === 0) {
          const repeatMonthDate = this.getRepeatMonthDate(
            frequency.monthly.repeatDate,
            frequency.monthly.repeatType,
            frequency.monthly.repeatWeekday,
            newDate,
            routineStartDate,
            futureDate
          );
          console.log("repeatMonthDate" + repeatMonthDate);
          if (
            repeatMonthDate !== null &&
            repeatMonthDate <= futureDate &&
            (createActivitiesInPast ? repeatMonthDate >= routineStartDate : repeatMonthDate >= now)
          ) {
            possibleDates.push(this.convertDateToString(repeatMonthDate));
          }

          date.setUTCMonth(date.getUTCMonth() + each);
        } else {
          date.setUTCMonth(date.getUTCMonth() + 1);
        }
      }
    } else if (frequency.yearly) {
      const months = frequency.yearly.months;
      const monthAfterFutureDate = this.addMonthToDate(futureDate, 1);
      //const date = new Date(now.getTime());
      const date = new Date(createActivitiesInPast ? routineStartDate.getTime() : now.getTime());
      date.setUTCDate(1);
      while (date <= monthAfterFutureDate) {
        const newDate = new Date(date.getTime());

        if ((newDate.getUTCFullYear() - routineStartDate.getUTCFullYear()) % each === 0 && months.indexOf(newDate.getUTCMonth()) !== -1) {
          const repeatMonthDate = this.getRepeatMonthDate(
            frequency.yearly.repeatDate,
            frequency.yearly.repeatType,
            frequency.yearly.repeatWeekday,
            newDate,
            routineStartDate,
            futureDate
          );
          console.log("repeatMonthDate" + repeatMonthDate);
          if (
            repeatMonthDate !== null &&
            repeatMonthDate <= futureDate &&
            (createActivitiesInPast ? repeatMonthDate >= routineStartDate : repeatMonthDate >= now)
          ) {
            possibleDates.push(this.convertDateToString(repeatMonthDate));
          }
        }
        date.setUTCMonth(date.getUTCMonth() + 1);
      }
    }
    return possibleDates;
  }

  static getRepeatMonthDate(
    repeatDate: number | undefined,
    repeatType: string | undefined,
    repeatWeekday: number | undefined,
    newDate: Date,
    startDate: Date,
    futureDate: Date
  ): Date | null {
    if (repeatDate) {
      return this.getDateInMonth(repeatDate, newDate.getUTCMonth(), newDate.getUTCFullYear());
    } else if (repeatType !== undefined && repeatWeekday !== undefined) {
      if (repeatType === "last") {
        const lastDayInMonth = this.getLastDayInMonth(repeatWeekday, newDate.getUTCMonth(), newDate.getUTCFullYear());
        if (lastDayInMonth >= startDate && lastDayInMonth <= futureDate) {
          return lastDayInMonth;
        }
      } else {
        const nthDayInMonth = this.getNthDayInMonth(+repeatType, repeatWeekday, newDate.getUTCMonth(), newDate.getUTCFullYear());
        if (nthDayInMonth >= startDate && nthDayInMonth <= futureDate) {
          return nthDayInMonth;
        }
      }
    }
    return null;
  }

  static monthDiff(d1: Date, d2: Date): number {
    let months: number;
    months = (d2.getUTCFullYear() - d1.getUTCFullYear()) * 12;
    months -= d1.getUTCMonth();
    months += d2.getUTCMonth();
    return months <= 0 ? 0 : months;
  }

  static getDateInMonth(d: number, month: number, year: number): Date | null {
    const date = new Date();
    date.setUTCDate(d);
    date.setUTCFullYear(year);
    date.setUTCMonth(month);
    if (date.getUTCMonth() === month) {
      return date;
    } else {
      date.setUTCDate(1);
      date.setUTCDate(date.getUTCDate() - 1);
      return date;
    }
  }

  static getFirstDayInMonth(day: number, month: number, year: number): Date {
    const dayIndex = new Date(year, month, 1).getUTCDay();
    const date = new Date();
    date.setUTCDate(1 + ((day - dayIndex + 7) % 7));
    date.setUTCFullYear(year);
    date.setUTCMonth(month);
    return date;
  }

  static getLastDayInMonth(day: number, month: number, year: number): Date {
    const date = new Date();
    date.setUTCDate(1);
    date.setUTCFullYear(year);
    date.setUTCMonth(month + 1);
    do {
      date.setUTCDate(date.getUTCDate() - 1);
    } while (date.getUTCDay() !== day);
    return date;
  }

  static getNthDayInMonth(n: number, day: number, month: number, year: number): Date {
    const date = this.getFirstDayInMonth(day, month, year);
    return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + (n - 1) * 7);
  }

  static convertFrequencyToString(frequency: IRoutineFrequency): string {
    const langId = getCurrentNormalizedLanguageCode();

    let weekdays = [
      t("main.sunday"),
      t("main.monday"),
      t("main.tuesday"),
      t("main.wednesday"),
      t("main.thursday"),
      t("main.friday"),
      t("main.saturday"),
    ];

    let frequencyString = (frequency.yearly ? t("main.eachForYear") : t("main.each")) + " " + (frequency.each > 1 ? frequency.each + ". " : "");
    if (frequency.daily) {
      frequencyString += t("main.day").toLowerCase();
    } else if (frequency.weekly) {
      frequencyString += t("main.week").toLowerCase();
      let days = frequency.weekly.days;
      if (days && days.length > 0) {
        days.forEach((day) => {
          let weekday = weekdays[day];
          if (!weekday) {
            console.log("weekday is undefined (" + weekday + ")");
          }
          frequencyString += ", " + (langId === "no" ? weekday?.toLowerCase() : weekday);
        });
      }
    } else if (frequency.monthly) {
      frequencyString += t("main.month").toLowerCase();
      if (frequency.monthly.repeatDate) {
        frequencyString += ", " + t("main.day") + " " + frequency.monthly.repeatDate;
      } else if (frequency.monthly.repeatType && frequency.monthly.repeatWeekday) {
        let repeatType = t("main.first");
        if (frequency.monthly.repeatType === "1") {
          repeatType = t("main.first");
        } else if (frequency.monthly.repeatType === "2") {
          repeatType = t("main.second");
        } else if (frequency.monthly.repeatType === "3") {
          repeatType = t("main.third");
        } else if (frequency.monthly.repeatType === "4") {
          repeatType = t("main.forth");
        } else if (frequency.monthly.repeatType === "last") {
          repeatType = t("main.last");
        }
        let weekday = weekdays[frequency.monthly.repeatWeekday];
        if (!weekday) {
          console.log("weekday is undefined (" + weekday + ")");
        }
        frequencyString +=
          ", " + t("main.theXWeekday").toLowerCase() + " " + repeatType.toLowerCase() + " " + (langId === "no" ? weekday?.toLowerCase() : weekday);
      }
    } else if (frequency.yearly) {
      frequencyString += t("main.year").toLowerCase();
      if (frequency.yearly.repeatDate) {
        frequencyString += ", " + t("main.day") + " " + frequency.yearly.repeatDate;
      } else if (frequency.yearly.repeatType && frequency.yearly.repeatWeekday) {
        let repeatType = t("main.first");
        if (frequency.yearly.repeatType === "1") {
          repeatType = t("main.first");
        } else if (frequency.yearly.repeatType === "2") {
          repeatType = t("main.second");
        } else if (frequency.yearly.repeatType === "3") {
          repeatType = t("main.third");
        } else if (frequency.yearly.repeatType === "4") {
          repeatType = t("main.forth");
        } else if (frequency.yearly.repeatType === "last") {
          repeatType = t("main.last");
        }
        let weekday = weekdays[frequency.yearly.repeatWeekday];
        if (!weekday) {
          console.log("weekday is undefined (" + frequency.yearly.repeatWeekday + ")");
        }
        frequencyString +=
          ", " + t("main.theXWeekday").toLowerCase() + " " + repeatType?.toLowerCase() + " " + (langId === "no" ? weekday?.toLowerCase() : weekday);
      }
      frequencyString += " " + t("main.in") + " ";
      const monthNames = [
        t("main.january"),
        t("main.february"),
        t("main.march"),
        t("main.april"),
        t("main.may"),
        t("main.june"),
        t("main.july"),
        t("main.august"),
        t("main.september"),
        t("main.october"),
        t("main.november"),
        t("main.december"),
      ];
      frequency.yearly.months.forEach((month, index) => {
        let monthString = monthNames[month];
        if (index > 0) frequencyString += ", ";
        if (!monthString) {
          console.log("monthString is undefined (" + month + ")");
        }
        frequencyString += langId === "no" ? monthString?.toLowerCase() : monthString;
      });
    } else if (frequency.running) {
      frequencyString = t("main.running");
    }
    return frequencyString;
  }
}
